import { ActionType } from '../../../../core/redux/actionType';
import { makeApiAction, makeRsaaTypes, ApiReducer, makeApiReducer } from '../../../../core/api/rsaa';
import { Endpoints } from '../../../../core/api/endpoints';
import { AppState } from '../../../../core/redux/appState';
import { getLoading, addError, noLoadingNoError, addLoading } from '../../../../core/api/module';


const KEY_ACTION = ActionType.SendDevItemCommentNotificationRequest;
const ACTIONS    = [
  ActionType.SendDevItemCommentNotificationRequest,
  ActionType.SendDevItemCommentNotificationResponse,
  ActionType.SendDevItemCommentNotificationError,
];


type Payload = string;


type Meta = {
  projectId: number;
  devItemId: number;
  devActionId: number;
};


/**
 * Dispatch this to send an email notifying the project participant of a new collaborator comment
 *
 * @param projectId - the database Id for the project that's been commented-on
 * @param participantId - the database Id for the participant receiving the comment
 * @param devItemId - the database Id for the commented dev item
 * @param devActionId - the database Id for the specific action in the dev item to which the comment is to be added
 * @param participantEmail - the participant's email address
 * @param collaboratorEmail - the collaborator's email address
 * @param otherCollaboratorEmails - email addresses for all other collaborators, who will also be notified
 * @param comment - the text content of the newly added comment
 * @param languageId - the language in which notifications should be sent (usually just the project default language)
 */
export const sendDevItemCommentNotificationAction = (
  projectId: number,
  participantId: number,
  devItemId: number,
  devActionId: number,
  participantEmail: string,
  collaboratorEmail: string,
  otherCollaboratorEmails: string[],
  comment: string,
  languageId: number,
) => makeApiAction<Payload, Meta>(
  {
    method:   'POST',
    endpoint: Endpoints.SendDevItemCommentNotification,
    headers:  { 'content-type': 'application/json' },
    body:     JSON.stringify(
      {
        projectId:         projectId,
        participantId:     participantId,
        comment:           comment,
        to: otherCollaboratorEmails.map(c => ({ toAddress: c, languageId: languageId }))
              .concat({ toAddress: participantEmail, languageId: languageId }),
        collaboratorEmail: collaboratorEmail,
      },
    ),
    types:    makeRsaaTypes<Payload, Meta>(
      ACTIONS,
      {
        projectId:   projectId,
        devItemId:   devItemId,
        devActionId: devActionId,
      },
    ),
    bailout:  (state: AppState) => !!getLoading(state, KEY_ACTION, `${projectId}-${devItemId}-${devActionId}`),
  },
);

const reducer: ApiReducer<Payload, Meta> = (state, action, errorMsg, isLoading, payload) => {
  const { projectId, devItemId, devActionId } = action.meta;

  const metaTag = `${projectId}-${devItemId}-${devActionId}`;

  if (isLoading) {
    return addLoading(state, KEY_ACTION, metaTag);
  }

  if (errorMsg) {
    return addError(state, KEY_ACTION, errorMsg, metaTag);
  }

  return noLoadingNoError(state, KEY_ACTION, metaTag);
};

export const sendDevItemCommentNotificationReducer = makeApiReducer<Payload, Meta>(ACTIONS, KEY_ACTION, reducer);
